/**
 * Exhaustive evaluation of the v0 condition grammar (D15) against live state
 * (architecture.md §7.1 `conditions/`).
 *
 * @remarks
 * Pure and synchronous: same condition + same bundle + same state ⇒ same
 * boolean, always. The `switch` is `never`-guarded (architecture.md §3.5) so
 * a new condition variant breaks compilation here before it can ship
 * half-evaluated. A condition referencing a flag the pack never declared is
 * a corrupt bundle — the loader's semantic gate rejects it at load, so
 * meeting one here throws {@link EngineInvariantError} rather than guessing.
 *
 * `item_in_room` reads the authored placements MINUS the player's deltas
 * (`removedRoomItemIds`): v0 has no effect that ADDS an item to a room, so
 * "still where the pack put it" is the complete truth.
 *
 * @packageDocumentation
 */

import type {
	AllOfCondition,
	AnyOfCondition,
	AtLeastOfCondition,
	ConditionDefinition,
	DirectObjectIsCondition,
	IndirectObjectIsCondition,
	ItemId,
	NotCondition,
	RuntimeState,
	ToolIsCondition,
} from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { CommandInHand } from './command-in-hand.js';

import { requireItem } from '../bundle/bundle-lookups.js';
import { isActorDefeated, playerHealthOf, wasActorAttacked } from '../entities/actor-combat.js';
import { isActorAddressable, isActorInRoom, isActorPresent } from '../entities/actor-presence.js';
import { counterValueOf } from '../entities/counter-state.js';
import { isConcealedItemFound, isContainerOpen, isItemSpent, isSwitchedOn } from '../entities/item-state.js';
import { EngineInvariantError } from '../execution/engine-invariant-error.js';
import { pendingOccurrencesOf } from '../execution/pending-occurrences.js';
import { isRecordHolding } from '../record/record-of-run.js';

/**
 * Evaluate one authored condition, recursively, of any v0 variant.
 *
 * @param condition - The authored condition
 * @param bundle - The loaded bundle (for placement lookups and flag-declaration checks)
 * @param state - The live runtime state
 * @param commandInHand - D219/D226/D229: the command being answered, when the caller is answering one; only the three sentence conditions (`direct_object_is`, `indirect_object_is`, `tool_is`) read it
 * @returns Whether the condition is currently satisfied
 */
export const evaluateCondition = (
	condition: ConditionDefinition,
	bundle: EngineGameBundle,
	state: RuntimeState,
	commandInHand?: CommandInHand,
): boolean => {
	switch (condition.type) {
		case 'all_of': {
			return condition.conditions.every((nested) => evaluateCondition(nested, bundle, state, commandInHand));
		}
		case 'any_of': {
			return condition.conditions.some((nested) => evaluateCondition(nested, bundle, state, commandInHand));
		}
		// D229 (E23): at least k of n — the general combinator the two above are
		// sugar for. Members are read in authored order until the count is met,
		// so a satisfied node never reads past what satisfied it.
		case 'at_least_of': {
			return holdsAtLeast(condition, bundle, state, commandInHand);
		}
		// D226 (E19): the sentence's FIRST object — what the command was about.
		// The twin of the arm below, on the other slot.
		case 'direct_object_is': {
			return slotNamesThing(commandInHand?.directObjectItemId, condition.itemId);
		}
		// D219: the sentence, not the world. With no command in hand there is
		// nothing to have been offered to, so it is false; with one, the named
		// thing must be the second object, or, with no thing named, any second
		// object at all will do.
		case 'indirect_object_is': {
			return slotNamesThing(commandInHand?.indirectObjectItemId, condition.itemId);
		}
		case 'not': {
			return !evaluateCondition(condition.condition, bundle, state, commandInHand);
		}
		// D229 (E22): the sentence's third slot — what the act was done WITH.
		case 'tool_is': {
			return slotNamesThing(commandInHand?.toolItemId, condition.itemId);
		}
		default: {
			return evaluateLeafCondition(condition, bundle, state);
		}
	}
};

/**
 * D229 (E23): do at least `minimumCount` of the members hold? Counted
 * member by member, in authored order, and stopped the moment the count is
 * reached — `all_of`'s and `any_of`'s short-circuits, generalized.
 *
 * @param condition - The count condition
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @param commandInHand - The command being answered, when there is one
 * @returns Whether the count is met
 */
const holdsAtLeast = (
	condition: AtLeastOfCondition,
	bundle: EngineGameBundle,
	state: RuntimeState,
	commandInHand: CommandInHand | undefined,
): boolean => {
	let satisfiedCount = 0;

	for (const member of condition.conditions) {
		if (evaluateCondition(member, bundle, state, commandInHand)) {
			satisfiedCount += 1;

			if (satisfiedCount >= condition.minimumCount) {
				return true;
			}
		}
	}

	return false;
};

/** The conditions that read the world alone: not the combinators, and not the three reads of the sentence (D219, D226, D229). */
type LeafCondition = Exclude<
	ConditionDefinition,
	AllOfCondition | AnyOfCondition | AtLeastOfCondition | DirectObjectIsCondition | IndirectObjectIsCondition | NotCondition | ToolIsCondition
>;

/**
 * The leaf reads, by the state region each one reads — the one roster the
 * leaf evaluator dispatches on (D233).
 *
 * @remarks
 * The leaf switch listed every variant as a case and dispatched the regions'
 * groups by case-fallthrough, which read well and cost one complexity point
 * per label; D233's twentieth leaf put it over the cap. Each region now
 * declares its own members as a list `satisfies` its type, so adding a
 * variant to a region is one entry here and one arm in that region's
 * evaluator, and a variant in NO region is a compile error in the leaf
 * evaluator's exhaustive remainder — the roster cannot go stale.
 */
const ITEM_CONDITION_TYPES = [
	'container_open',
	'has_item',
	'item_acquired',
	'item_equipped',
	'item_found',
	'item_in_room',
	'item_searched',
	'item_spent',
	'switched_on',
] as const satisfies readonly LeafCondition['type'][];

const ACTOR_CONDITION_TYPES = ['actor_addressable', 'actor_attacked', 'actor_defeated', 'actor_in_room', 'actor_present'] as const satisfies readonly LeafCondition['type'][];

const NARRATIVE_CONDITION_TYPES = ['ending_selected', 'log_read', 'occurrence_tally_at_least', 'voice_line_spoken'] as const satisfies readonly LeafCondition['type'][];

const SCHEDULER_CONDITION_TYPES = ['event_fired', 'event_pending'] as const satisfies readonly LeafCondition['type'][];

/** Whether a leaf's discriminant is one of a region's members. */
const isOneOf = <const Member extends LeafCondition['type']>(
	members: readonly Member[],
	condition: LeafCondition,
): condition is Extract<LeafCondition, { readonly type: Member }> => (members as readonly string[]).includes(condition.type);

/** The conditions that read one ITEM's standing in the world. */
type ItemCondition = Extract<LeafCondition, { readonly type: (typeof ITEM_CONDITION_TYPES)[number] }>;

/**
 * Evaluate one item-shaped condition — where is it, who has it, what has
 * happened to it, and (D233 moved the two door-and-switch reads here at the
 * complexity cap) whether it stands open or runs.
 *
 * @param condition - The item condition
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns Whether the condition is currently satisfied
 */
const evaluateItemCondition = (condition: ItemCondition, bundle: EngineGameBundle, state: RuntimeState): boolean => {
	switch (condition.type) {
		// D55: the reading `openable`'s per-state prose desugars into, and the one
		// any author can now write directly. Shared with catalog derivation so a
		// door cannot mean two things.
		case 'container_open': {
			return isContainerOpen(requireItem(bundle, condition.itemId), state);
		}
		case 'has_item': {
			return state.world.inventoryItemIds.includes(condition.itemId);
		}
		// D146: ever taken — history, not location; the record is write-once.
		case 'item_acquired': {
			return state.world.acquisitions.some((acquisition) => acquisition.itemId === condition.itemId);
		}
		// D143: worn, not merely carried — the mask on the face, the knife in hand.
		case 'item_equipped': {
			return Object.values(state.world.equippedItemIds).includes(condition.itemId);
		}
		// D239: the concealed thing found — the one predicate the catalog's
		// concealment gate reads, so a condition cannot call a thing found
		// that the player cannot yet name.
		case 'item_found': {
			return isConcealedItemFound(requireItem(bundle, condition.itemId), state);
		}
		case 'item_in_room': {
			const initialPlacements = bundle.indexes.initialItemIdsByRoomId.get(condition.roomId) ?? [];

			return initialPlacements.includes(condition.itemId) && !state.world.removedRoomItemIds.includes(condition.itemId);
		}
		// D158: gone THROUGH, not merely opened. Reads the same set the entity
		// catalog reads for concealment, so a condition and the catalog cannot
		// disagree about whether a thing has been found.
		case 'item_searched': {
			return state.world.searchedContainerItemIds.includes(condition.itemId);
		}
		// D152: the declared uses all gone — the strap broken, the knife snapped.
		case 'item_spent': {
			return isItemSpent(requireItem(bundle, condition.itemId), state);
		}
		// D56: what `switchable` is FOR, from the describer's side — the reading
		// that lets a kettle look different while it is running. D65 sharpened
		// it: `switched_on` means RUNNING — position on AND powered — because
		// "the switch is still over and the room has gone quiet" is the beat the
		// power model exists to express, and a variant keyed here must go dark
		// with the mains even while the switch stays on.
		case 'switched_on': {
			return isRunning(condition.itemId, bundle, state);
		}
	}
};

/** The actor-shaped conditions — where a body is, and whether the player can reach or address it. */
type ActorCondition = Extract<LeafCondition, { readonly type: (typeof ACTOR_CONDITION_TYPES)[number] }>;

/**
 * Evaluate one condition about a BODY.
 *
 * @remarks
 * D232's two answers to "is the actor here" — within earshot, and within
 * reach — D233's third, where it STANDS, whoever is looking, and D234's
 * two about a fight: struck ever, fallen now. All of them
 * read the one presence module the catalog and the handlers read (the
 * fight's, the one body module the strike and the tick read), so a
 * condition and the catalog cannot disagree about where a body is. (The
 * module reads this evaluator for `visibleWhen`; the cycle is call-time
 * only, like the intercepts' with the roster.) Split out at the complexity
 * cap when D233 added the third, exactly as the scheduler's reads were.
 *
 * @param condition - The actor-shaped condition
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns Whether the condition is currently satisfied
 */
const evaluateActorCondition = (condition: ActorCondition, bundle: EngineGameBundle, state: RuntimeState): boolean => {
	switch (condition.type) {
		case 'actor_addressable': {
			return isActorAddressable(bundle, state, condition.actorId);
		}
		// D234: the two facts a fight writes — struck ever, fallen now.
		case 'actor_attacked': {
			return wasActorAttacked(state, condition.actorId);
		}
		case 'actor_defeated': {
			return isActorDefeated(bundle, state, condition.actorId);
		}
		case 'actor_in_room': {
			return isActorInRoom(bundle, state, condition.actorId, condition.roomId);
		}
		case 'actor_present': {
			return isActorPresent(bundle, state, condition.actorId);
		}
	}
};

/** The scheduler-shaped conditions — what has fired, and what is still coming. */
type SchedulerCondition = Extract<LeafCondition, { readonly type: (typeof SCHEDULER_CONDITION_TYPES)[number] }>;

/**
 * Evaluate one condition about TIME.
 *
 * @remarks
 * R11's `event_fired` is the audit trail — fired, not merely processed, so a
 * dropped occurrence promises nothing. D158's `event_pending` is its mirror:
 * armed and still coming. Together they are the two questions an author can
 * ask about a scheduled thing, and neither needs a flag maintained alongside
 * the queue that already answers them.
 *
 * @param condition - The scheduler-shaped condition
 * @param state - The live runtime state
 * @returns Whether the condition is currently satisfied
 */
const evaluateSchedulerCondition = (condition: SchedulerCondition, state: RuntimeState): boolean =>
	condition.type === 'event_fired'
		? state.scheduler.firedEventIds.includes(condition.eventId)
		: pendingOccurrencesOf(state, condition.eventId).length > 0;

/** The narrative-state conditions — what the story remembers about itself. */
type NarrativeCondition = Extract<LeafCondition, { readonly type: (typeof NARRATIVE_CONDITION_TYPES)[number] }>;

/**
 * Evaluate one condition about what the STORY remembers.
 *
 * @remarks
 * Four reads of `state.narrative`, split out at the complexity cap the way
 * the item-shaped ones were: D158 added the sixteenth and seventeenth
 * variants to the grammar and the leaf switch stopped fitting. The split is
 * by state REGION rather than by anything cleverer, so a reader looking for
 * where `log_read` is decided finds it under the region it reads.
 *
 * @param condition - The narrative-shaped condition
 * @param state - The live runtime state
 * @returns Whether the condition is currently satisfied
 */
const evaluateNarrativeCondition = (condition: NarrativeCondition, state: RuntimeState): boolean => {
	switch (condition.type) {
		// R11: a named ending, or ANY when the id is omitted. Reads the
		// selection directly so nothing invents a flag that shadows it — and
		// D151 made it a HISTORY, so "any" means the list is non-empty.
		case 'ending_selected': {
			return condition.endingId === undefined
				? state.narrative.selectedEndingIds.length > 0
				: state.narrative.selectedEndingIds.includes(condition.endingId);
		}
		// R12: has the named document been read? First inspect of its carrier
		// marks it; a reread never clears the mark.
		case 'log_read': {
			return state.narrative.readLogIds.includes(condition.logId);
		}
		// R12: the repeated-behavior predicate — reads the engine-maintained
		// tallies, which count whether or not anything reads them.
		case 'occurrence_tally_at_least': {
			return (state.narrative.occurrenceTallies[condition.tallyKey] ?? 0) >= condition.minimumCount;
		}
		case 'voice_line_spoken': {
			return (state.narrative.voiceLineUsage[condition.voiceLineId] ?? 0) > 0;
		}
	}
};

/**
 * Evaluate one LEAF condition — every variant that reads the world directly.
 *
 * @param condition - The leaf condition
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns Whether the condition is currently satisfied
 */
const evaluateLeafCondition = (condition: LeafCondition, bundle: EngineGameBundle, state: RuntimeState): boolean => {
	// The reads live together by state REGION, one evaluator each — a reader
	// looking for where `log_read` is decided finds it under the region it reads.
	if (isOneOf(ACTOR_CONDITION_TYPES, condition)) {
		return evaluateActorCondition(condition, bundle, state);
	}

	if (isOneOf(ITEM_CONDITION_TYPES, condition)) {
		return evaluateItemCondition(condition, bundle, state);
	}

	if (isOneOf(NARRATIVE_CONDITION_TYPES, condition)) {
		return evaluateNarrativeCondition(condition, state);
	}

	if (isOneOf(SCHEDULER_CONDITION_TYPES, condition)) {
		return evaluateSchedulerCondition(condition, state);
	}

	switch (condition.type) {
		// D244: an amount at a threshold — the moved value, or the declared start.
		case 'counter_at_least': {
			return counterValueOf(bundle, state, condition.counterId) >= condition.minimumCount;
		}
		case 'flag_equals': {
			const isFlagSet = state.world.flagValues[condition.flagId];

			if (isFlagSet === undefined) {
				throw new EngineInvariantError(
					`Condition references flag "${condition.flagId}" which is not in the runtime flag record — the loader's semantic gate should have rejected this bundle`,
				);
			}

			return isFlagSet === condition.expectedValue;
		}
		// D244: what is left of the player's body — none at all, in a pack with no body, holds no threshold.
		case 'player_health_at_least': {
			return (playerHealthOf(bundle, state) ?? 0) >= condition.minimumCount;
		}
		// D241: the Record this run began with — never what it has written since.
		case 'record_holds': {
			return isRecordHolding(condition, bundle, state);
		}
		case 'room_equals': {
			return state.progression.activeRoomId === condition.roomId;
		}
	}
};

/**
 * D56/D65: RUNNING means position on AND powered — a variant keyed on
 * `switched_on` must go dark with the mains even while the switch stays on.
 *
 * @param itemId - The device
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns Whether the device is currently running
 */
const isRunning = (itemId: ItemId, bundle: EngineGameBundle, state: RuntimeState): boolean => {
	const device = requireItem(bundle, itemId);
	const isPowered =
		device.switchable?.poweredWhen === undefined || evaluateCondition(device.switchable.poweredWhen, bundle, state);

	return isPowered && isSwitchedOn(device, state);
};

/**
 * Does a slot of the command in hand name the thing a condition asks after
 * (D219, D226, D229)? With no command in hand the slot names nothing, so it is
 * false; with one, the named thing must be the one asked after, or, when the
 * condition names none, any thing at all will do.
 *
 * @param namedItemId - The item the slot names, if any
 * @param askedItemId - The item the condition names, if any
 * @returns Whether the slot satisfies the condition
 */
const slotNamesThing = (namedItemId: ItemId | undefined, askedItemId: ItemId | undefined): boolean =>
	askedItemId === undefined ? namedItemId !== undefined : namedItemId === askedItemId;
